import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { useToast } from '../hooks/use-toast';
import { 
  Calendar, Clock, AlertTriangle, CheckCircle, Bell, 
  Plus, ChevronLeft, ChevronRight, FileText, Shield,
  Users, Building, Wrench, GraduationCap, ClipboardCheck,
  AlertCircle, Filter, Mail
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Compliance item types with icons and colors
const COMPLIANCE_TYPES = {
  training: { label: 'Training Certification', icon: GraduationCap, color: 'bg-blue-100 text-blue-800', borderColor: 'border-l-blue-500' },
  permit: { label: 'Permit', icon: ClipboardCheck, color: 'bg-green-100 text-green-800', borderColor: 'border-l-green-500' },
  inspection: { label: 'Inspection', icon: Shield, color: 'bg-purple-100 text-purple-800', borderColor: 'border-l-purple-500' },
  equipment: { label: 'Equipment Certification', icon: Wrench, color: 'bg-orange-100 text-orange-800', borderColor: 'border-l-orange-500' },
  medical: { label: 'Medical Exam', icon: Users, color: 'bg-pink-100 text-pink-800', borderColor: 'border-l-pink-500' },
  license: { label: 'License/Registration', icon: FileText, color: 'bg-cyan-100 text-cyan-800', borderColor: 'border-l-cyan-500' },
  audit: { label: 'Safety Audit', icon: Building, color: 'bg-yellow-100 text-yellow-800', borderColor: 'border-l-yellow-500' },
  osha: { label: 'OSHA Requirement', icon: AlertTriangle, color: 'bg-red-100 text-red-800', borderColor: 'border-l-red-500' }
};

const RECURRENCE_OPTIONS = [
  { value: 'none', label: 'One-time' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'semi-annual', label: 'Semi-Annual' },
  { value: 'annual', label: 'Annual' },
  { value: 'biennial', label: 'Every 2 Years' },
  { value: 'triennial', label: 'Every 3 Years' }
];

const ComplianceCalendarPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    title: '',
    type: 'training',
    description: '',
    due_date: '',
    assigned_to: '',
    recurrence: 'annual',
    reminder_days: 30,
    notes: ''
  });

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/compliance/deadlines`);
      setItems(response.data.items || []);
    } catch (error) {
      console.error('Error fetching compliance items:', error);
      // Demo data for testing
      setItems([
        { id: '1', title: 'Forklift Operator Certification Renewal', type: 'training', due_date: getDateString(7), assigned_to: 'John Smith', status: 'upcoming', recurrence: 'triennial' },
        { id: '2', title: 'Fire Extinguisher Annual Inspection', type: 'inspection', due_date: getDateString(3), assigned_to: 'Safety Team', status: 'urgent', recurrence: 'annual' },
        { id: '3', title: 'Hot Work Permit Expires', type: 'permit', due_date: getDateString(1), assigned_to: 'Site Supervisor', status: 'critical', recurrence: 'none' },
        { id: '4', title: 'OSHA 300A Posting Deadline', type: 'osha', due_date: '2026-02-01', assigned_to: 'HR Manager', status: 'upcoming', recurrence: 'annual' },
        { id: '5', title: 'Crane Annual Inspection', type: 'equipment', due_date: getDateString(14), assigned_to: 'Maintenance', status: 'upcoming', recurrence: 'annual' },
        { id: '6', title: 'First Aid/CPR Certification', type: 'training', due_date: getDateString(-5), assigned_to: 'All Employees', status: 'overdue', recurrence: 'biennial' },
        { id: '7', title: 'Hearing Conservation Testing', type: 'medical', due_date: getDateString(30), assigned_to: 'Production Team', status: 'upcoming', recurrence: 'annual' },
        { id: '8', title: 'Hazmat License Renewal', type: 'license', due_date: getDateString(45), assigned_to: 'Transport Dept', status: 'upcoming', recurrence: 'annual' }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const getDateString = (daysFromNow) => {
    const date = new Date();
    date.setDate(date.getDate() + daysFromNow);
    return date.toISOString().split('T')[0];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await axios.put(`${API}/api/compliance/deadlines/${editingItem.id}`, formData);
        toast({ title: 'Updated', description: 'Compliance item updated' });
      } else {
        await axios.post(`${API}/api/compliance/deadlines`, formData);
        toast({ title: 'Added', description: 'Compliance deadline added' });
      }
      setShowAddDialog(false);
      resetForm();
      fetchItems();
    } catch (error) {
      // For demo, add locally
      const newItem = {
        id: String(Date.now()),
        ...formData,
        status: getItemStatus(formData.due_date)
      };
      setItems([...items, newItem]);
      setShowAddDialog(false);
      resetForm();
      toast({ title: 'Added', description: 'Compliance deadline added' });
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      type: 'training',
      description: '',
      due_date: '',
      assigned_to: '',
      recurrence: 'annual',
      reminder_days: 30,
      notes: ''
    });
    setEditingItem(null);
  };

  const getItemStatus = (dueDate) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(dueDate);
    const diffDays = Math.ceil((due - today) / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return 'overdue';
    if (diffDays <= 3) return 'critical';
    if (diffDays <= 7) return 'urgent';
    if (diffDays <= 30) return 'upcoming';
    return 'scheduled';
  };

  const getStatusBadge = (status) => {
    const styles = {
      overdue: 'bg-red-600 text-white',
      critical: 'bg-red-100 text-red-800',
      urgent: 'bg-orange-100 text-orange-800',
      upcoming: 'bg-yellow-100 text-yellow-800',
      scheduled: 'bg-gray-100 text-gray-800',
      completed: 'bg-green-100 text-green-800'
    };
    return styles[status] || styles.scheduled;
  };

  const getDaysUntilDue = (dueDate) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(dueDate);
    return Math.ceil((due - today) / (1000 * 60 * 60 * 24));
  };

  const markComplete = async (itemId) => {
    setItems(items.map(item => 
      item.id === itemId ? { ...item, status: 'completed', completed_date: new Date().toISOString() } : item
    ));
    toast({ title: 'Completed', description: 'Item marked as complete' });
  };

  // Calendar helpers
  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const getMonthName = (date) => {
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const previousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const getItemsForDate = (day) => {
    const dateStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return items.filter(item => item.due_date === dateStr);
  };

  const filteredItems = items.filter(item => {
    if (filterType !== 'all' && item.type !== filterType) return false;
    if (filterStatus !== 'all' && item.status !== filterStatus) return false;
    return true;
  }).sort((a, b) => new Date(a.due_date) - new Date(b.due_date));

  const upcomingCount = items.filter(i => ['upcoming', 'urgent', 'critical'].includes(i.status)).length;
  const overdueCount = items.filter(i => i.status === 'overdue').length;
  const criticalCount = items.filter(i => ['critical', 'overdue'].includes(i.status)).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-700 via-indigo-600 to-purple-700 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/10 rounded-lg">
              <Calendar className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold">Compliance Calendar</h1>
              <p className="text-sm text-indigo-100">Track certifications, permits, and regulatory deadlines</p>
            </div>
          </div>
          <Button onClick={() => { resetForm(); setShowAddDialog(true); }} className="bg-white text-indigo-700 hover:bg-indigo-50">
            <Plus className="w-4 h-4 mr-2" /> Add Deadline
          </Button>
        </div>
      </div>

      {/* Alert Banner */}
      {criticalCount > 0 && (
        <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-6 h-6 text-red-600" />
            <div>
              <p className="font-semibold text-red-800">Attention Required!</p>
              <p className="text-sm text-red-600">{criticalCount} item(s) need immediate attention ({overdueCount} overdue)</p>
            </div>
          </div>
          <Button variant="outline" className="border-red-300 text-red-700 hover:bg-red-100">
            <Bell className="w-4 h-4 mr-2" /> Send Reminders
          </Button>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Total Items</p>
                <p className="text-2xl font-bold">{items.length}</p>
              </div>
              <FileText className="w-6 h-6 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card className={overdueCount > 0 ? 'border-red-300 bg-red-50' : ''}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Overdue</p>
                <p className="text-2xl font-bold text-red-600">{overdueCount}</p>
              </div>
              <AlertTriangle className="w-6 h-6 text-red-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Due This Week</p>
                <p className="text-2xl font-bold text-orange-600">
                  {items.filter(i => getDaysUntilDue(i.due_date) >= 0 && getDaysUntilDue(i.due_date) <= 7).length}
                </p>
              </div>
              <Clock className="w-6 h-6 text-orange-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Completed</p>
                <p className="text-2xl font-bold text-green-600">
                  {items.filter(i => i.status === 'completed').length}
                </p>
              </div>
              <CheckCircle className="w-6 h-6 text-green-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Tabs defaultValue="calendar" className="space-y-4">
        <TabsList>
          <TabsTrigger value="calendar">Calendar View</TabsTrigger>
          <TabsTrigger value="list">List View</TabsTrigger>
          <TabsTrigger value="upcoming">Upcoming ({upcomingCount})</TabsTrigger>
        </TabsList>

        <TabsContent value="calendar">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <div className="flex items-center gap-4">
                <Button variant="outline" size="sm" onClick={previousMonth}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <h3 className="text-lg font-semibold">{getMonthName(currentMonth)}</h3>
                <Button variant="outline" size="sm" onClick={nextMonth}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
              <Button variant="outline" size="sm" onClick={() => setCurrentMonth(new Date())}>
                Today
              </Button>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-7 gap-1">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                  <div key={day} className="p-2 text-center text-sm font-medium text-gray-500">
                    {day}
                  </div>
                ))}
                {Array.from({ length: getFirstDayOfMonth(currentMonth) }).map((_, i) => (
                  <div key={`empty-${i}`} className="p-2 min-h-[80px]"></div>
                ))}
                {Array.from({ length: getDaysInMonth(currentMonth) }).map((_, i) => {
                  const day = i + 1;
                  const dayItems = getItemsForDate(day);
                  const isToday = new Date().toDateString() === new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day).toDateString();
                  
                  return (
                    <div 
                      key={day} 
                      className={`p-1 min-h-[80px] border rounded-lg cursor-pointer hover:bg-gray-50 ${isToday ? 'bg-blue-50 border-blue-300' : ''}`}
                      onClick={() => setSelectedDate({ day, items: dayItems })}
                    >
                      <div className={`text-sm font-medium ${isToday ? 'text-blue-600' : ''}`}>{day}</div>
                      <div className="space-y-1 mt-1">
                        {dayItems.slice(0, 2).map(item => {
                          const TypeIcon = COMPLIANCE_TYPES[item.type]?.icon || FileText;
                          return (
                            <div key={item.id} className={`text-xs p-1 rounded truncate ${COMPLIANCE_TYPES[item.type]?.color}`}>
                              <TypeIcon className="w-3 h-3 inline mr-1" />
                              {item.title.slice(0, 15)}...
                            </div>
                          );
                        })}
                        {dayItems.length > 2 && (
                          <div className="text-xs text-gray-500">+{dayItems.length - 2} more</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="list">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>All Compliance Items</CardTitle>
              <div className="flex gap-2">
                <Select value={filterType} onValueChange={setFilterType}>
                  <SelectTrigger className="w-40">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    {Object.entries(COMPLIANCE_TYPES).map(([key, { label }]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="overdue">Overdue</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                    <SelectItem value="upcoming">Upcoming</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {filteredItems.map(item => {
                  const TypeIcon = COMPLIANCE_TYPES[item.type]?.icon || FileText;
                  const daysUntil = getDaysUntilDue(item.due_date);
                  
                  return (
                    <div 
                      key={item.id} 
                      className={`p-4 border rounded-lg border-l-4 ${COMPLIANCE_TYPES[item.type]?.borderColor} hover:shadow-md transition-shadow`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <div className={`p-2 rounded-lg ${COMPLIANCE_TYPES[item.type]?.color}`}>
                            <TypeIcon className="w-5 h-5" />
                          </div>
                          <div>
                            <h4 className="font-medium">{item.title}</h4>
                            <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {new Date(item.due_date).toLocaleDateString()}
                              </span>
                              <span className="flex items-center gap-1">
                                <Users className="w-3 h-3" />
                                {item.assigned_to}
                              </span>
                              {item.recurrence !== 'none' && (
                                <Badge variant="outline" className="text-xs">
                                  {RECURRENCE_OPTIONS.find(r => r.value === item.recurrence)?.label}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={getStatusBadge(item.status)}>
                            {daysUntil < 0 ? `${Math.abs(daysUntil)} days overdue` : 
                             daysUntil === 0 ? 'Due today' : 
                             `${daysUntil} days`}
                          </Badge>
                          {item.status !== 'completed' && (
                            <Button variant="outline" size="sm" onClick={() => markComplete(item.id)}>
                              <CheckCircle className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="upcoming">
          <div className="space-y-4">
            {/* Critical/Overdue */}
            {items.filter(i => ['overdue', 'critical'].includes(i.status)).length > 0 && (
              <Card className="border-red-300">
                <CardHeader className="bg-red-50">
                  <CardTitle className="text-red-800 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" /> Requires Immediate Attention
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4 space-y-3">
                  {items.filter(i => ['overdue', 'critical'].includes(i.status)).map(item => {
                    const TypeIcon = COMPLIANCE_TYPES[item.type]?.icon || FileText;
                    const daysUntil = getDaysUntilDue(item.due_date);
                    
                    return (
                      <div key={item.id} className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-200">
                        <div className="flex items-center gap-3">
                          <TypeIcon className="w-5 h-5 text-red-600" />
                          <div>
                            <p className="font-medium">{item.title}</p>
                            <p className="text-sm text-red-600">
                              {daysUntil < 0 ? `${Math.abs(daysUntil)} days overdue` : `Due in ${daysUntil} days`}
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" className="border-red-300">
                            <Mail className="w-4 h-4 mr-1" /> Remind
                          </Button>
                          <Button size="sm" onClick={() => markComplete(item.id)}>
                            Complete
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            )}

            {/* This Week */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-orange-500" /> Due This Week
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {items.filter(i => {
                  const days = getDaysUntilDue(i.due_date);
                  return days >= 0 && days <= 7 && i.status !== 'overdue';
                }).map(item => {
                  const TypeIcon = COMPLIANCE_TYPES[item.type]?.icon || FileText;
                  return (
                    <div key={item.id} className={`flex items-center justify-between p-3 rounded-lg border-l-4 ${COMPLIANCE_TYPES[item.type]?.borderColor} bg-gray-50`}>
                      <div className="flex items-center gap-3">
                        <TypeIcon className="w-5 h-5" />
                        <div>
                          <p className="font-medium">{item.title}</p>
                          <p className="text-sm text-gray-500">{item.assigned_to} • {new Date(item.due_date).toLocaleDateString()}</p>
                        </div>
                      </div>
                      <Badge className={getStatusBadge(item.status)}>
                        {getDaysUntilDue(item.due_date)} days
                      </Badge>
                    </div>
                  );
                })}
                {items.filter(i => getDaysUntilDue(i.due_date) >= 0 && getDaysUntilDue(i.due_date) <= 7).length === 0 && (
                  <p className="text-gray-500 text-center py-4">No items due this week</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Add/Edit Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Edit Compliance Item' : 'Add Compliance Deadline'}</DialogTitle>
            <DialogDescription>Track certifications, permits, and regulatory requirements</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Title *</Label>
              <Input 
                value={formData.title} 
                onChange={e => setFormData({...formData, title: e.target.value})} 
                placeholder="e.g., Forklift Certification Renewal"
                required 
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Type</Label>
                <Select value={formData.type} onValueChange={v => setFormData({...formData, type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(COMPLIANCE_TYPES).map(([key, { label }]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Due Date *</Label>
                <Input 
                  type="date" 
                  value={formData.due_date} 
                  onChange={e => setFormData({...formData, due_date: e.target.value})} 
                  required 
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Assigned To</Label>
                <Input 
                  value={formData.assigned_to} 
                  onChange={e => setFormData({...formData, assigned_to: e.target.value})} 
                  placeholder="Person or team"
                />
              </div>
              <div>
                <Label>Recurrence</Label>
                <Select value={formData.recurrence} onValueChange={v => setFormData({...formData, recurrence: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {RECURRENCE_OPTIONS.map(r => (
                      <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Reminder (days before due)</Label>
              <Input 
                type="number" 
                min="1" 
                value={formData.reminder_days} 
                onChange={e => setFormData({...formData, reminder_days: parseInt(e.target.value)})} 
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Input 
                value={formData.notes} 
                onChange={e => setFormData({...formData, notes: e.target.value})} 
                placeholder="Additional details..."
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
              <Button type="submit">{editingItem ? 'Update' : 'Add Deadline'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Date Details Dialog */}
      <Dialog open={!!selectedDate} onOpenChange={() => setSelectedDate(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {selectedDate && `${getMonthName(currentMonth).split(' ')[0]} ${selectedDate.day}, ${currentMonth.getFullYear()}`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {selectedDate?.items?.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No items due on this date</p>
            ) : (
              selectedDate?.items?.map(item => {
                const TypeIcon = COMPLIANCE_TYPES[item.type]?.icon || FileText;
                return (
                  <div key={item.id} className={`p-3 rounded-lg border-l-4 ${COMPLIANCE_TYPES[item.type]?.borderColor} bg-gray-50`}>
                    <div className="flex items-center gap-2">
                      <TypeIcon className="w-5 h-5" />
                      <span className="font-medium">{item.title}</span>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">{item.assigned_to}</p>
                    <Badge className={`mt-2 ${COMPLIANCE_TYPES[item.type]?.color}`}>
                      {COMPLIANCE_TYPES[item.type]?.label}
                    </Badge>
                  </div>
                );
              })
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ComplianceCalendarPage;
